import React, { useEffect, useRef, useState } from "react";
import TopMostPortal from "../../components/ui/utils/Portals";
import { observer } from "mobx-react-lite";

import Select from "react-select";
import { XACTIMATE_CATEGORIES, FORMULA_UNIT_VALUES } from "@rebuild/shared";
import { collection, CollectionReference } from "firebase/firestore";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import SharedUtils from "../../shared/common/SharedUtils";
import { IDatabaseLineItem, IOnElementValueClickCBData, LineItemPreviewPanel } from "./LineItemDBCreationToolPage";
import MappingUtils from "./MappingTool/utils/MappingUtils";
import { logDebug } from "../../shared/common/logger";
import { v4 as uuidv4 } from "uuid";
import { toastNotify } from "./MappingTool/components/MappingWizardStep4";
import RestAPI from "../../utils/restAPI";
import { IXactimateToolRowData } from "../../shared/common/Azure";
import LoadingAnimation from "../../components/ui/utils/LoadingAnimation";
import MappingTopBox from "./MappingTool/components/MappingTopBox";
import { DuplicateMappingDialog, DuplicateMappingDialogData } from "../../components/DuplicateMappingDialog";
import { findDuplicateMapping } from "../../utils/findDuplicateMapping";

interface Option {
  value: string;
  label: string;
}

const MAPPINGS_LIMIT = 50;

const fetchMappingsPage = async (page: number): Promise<{ data: IXactimateToolRowData[]; total: number }> => {
  const json = await RestAPI.fetchRebuildAPI(
    "/rest/tools/xactimateMappings",
    "GET",
    { page, limit: MAPPINGS_LIMIT },
    "",
  );
  return { data: json.data ?? [], total: json.total ?? 0 };
};

const ComboBox: React.FC<{ startValue?: string; onChange: (val: string) => void }> = ({ startValue, onChange }) => {
  const [selectedOption, setSelectedOption] = useState<Option | null>(
    startValue
      ? {
          value: startValue,
          label: startValue,
        }
      : null,
  );

  const handleChange = (selected: Option | null) => {
    setSelectedOption(selected);
    onChange(selected!.value);
  };

  return (
    <Select
      options={XACTIMATE_CATEGORIES.map((m) => ({ value: m, label: m }))}
      value={selectedOption}
      onChange={handleChange}
      isClearable={false}
      placeholder="Category"
    />
  );
};

const LineItemPreviewPanelXactimate: React.FC<{
  xactimateRowData: IXactimateToolRowData;
  onClose: () => void;
  onAccept: (val: IDatabaseLineItem) => void;
  lineItemsDataFromDataBase: IDatabaseLineItem[];
}> = observer(({ xactimateRowData, onClose, lineItemsDataFromDataBase, onAccept }) => {
  const [searchLineItemInput, setSearchLineItemInput] = useState("");
  const [selectedLineItemIndex, setSelectedLineItemIndex] = useState(0);
  const [selectedLineItemId, setSelectedLineItemId] = useState(xactimateRowData.selectedLineItemId);
  const [selectedLineItem, setSelectedLineItem] = useState<IDatabaseLineItem>();

  useEffect(() => {
    logDebug("selectedLineItemId", selectedLineItemId);
    if (!selectedLineItemId) return;
    const li = lineItemsDataFromDataBase.find((li) => li.id === selectedLineItemId);
    const liClone = SharedUtils.cloneT<IDatabaseLineItem>(li);
    liClone.elements = liClone.elements.map((e) => {
      if (e.type === "text") {
        return e;
      } else {
        let value = e.value;
        if (e.defaultValue) {
          value = e.defaultValue;
        } else if (e.allowEmptyValue) {
          value = "";
        }
        const isRemoveAndReplace =
          e.acceptableValues && e.acceptableValues?.findIndex((v) => v.toLowerCase() === "remove and replace") >= 0;
        if (isRemoveAndReplace) {
          switch (xactimateRowData.activity) {
            case "-":
              value = "remove";
              break;
            case "+":
              value = "replace";
              break;
            case "&":
              value = "Remove and Replace";
              break;
            case "m":
              value = "material only";
              break;
            case "i":
              value = "install only";
              break;
            case "r":
              value = "detach and reset";
              break;
          }
        }
        return {
          ...e,
          value: value,
        };
      }
    });
    setSelectedLineItem(liClone);
  }, [selectedLineItemId]);

  function onElementValueClick(val: IOnElementValueClickCBData) {
    selectedLineItem!.elements[val.elementIndex].value = val.value;
    setSelectedLineItem({ ...selectedLineItem! });
  }

  const getOptions = (
    _lineItemsDataFromDataBase: IDatabaseLineItem[],
    _searchLineItemInput: string,
    _selectedLineItem: IDatabaseLineItem[],
  ) => {
    _lineItemsDataFromDataBase = _lineItemsDataFromDataBase.filter((li) => !li.deleted);
    const options = MappingUtils.filterDatabase(_lineItemsDataFromDataBase, _searchLineItemInput).filter(
      (li) => !_selectedLineItem.find((sli) => sli.id === li.id),
    );
    return options;
  };

  const getAutoComplateResults = (
    _lineItemsDataFromDataBase: IDatabaseLineItem[],
    _searchLineItemInput: string,
    _selectedLineItem: IDatabaseLineItem[],
    _selectedLineItemIndex: number,
  ) => {
    const options = getOptions(_lineItemsDataFromDataBase, _searchLineItemInput, _selectedLineItem);
    if (options.length === 0) return <div className="px-1.5 text-gray-300">No results found</div>;
    return (
      <div className="h-64 overflow-y-auto">
        {options.map((lineItem, index) => {
          return (
            <div
              key={lineItem.id}
              className="cursor-pointer"
              onClick={() => {
                logDebug("selected line item", lineItem);
                setSelectedLineItemId(lineItem.id);
                setSearchLineItemInput("");
              }}
            >
              <div
                className={`w-full px-1.5 py-0.5 justify-start items-center inline-flex ${
                  _selectedLineItemIndex === index ? "bg-gray-100" : ""
                }  hover:bg-gray-100 w-full`}
              >
                <div className="grow shrink basis-0 pl-2 pr-2.5 py-1.5 rounded-md flex-col justify-start items-start gap-2 inline-flex">
                  <div className="self-stretch justify-start items-center gap-2 inline-flex">
                    <div className="text-gray-900 text-base font-medium font-['Hanken Grotesk'] leading-normal  ">
                      {lineItem.originalLineItem}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-[1000px] h-[400px] bg-white rounded-lg relative overflow-y-scroll">
      <button
        className="absolute top-0 right-2 bg-sky-100 px-2 py-1 rounded-lg"
        onClick={() => {
          onClose();
        }}
      >
        Dismiss
      </button>
      <input
        className="w-full p-1 border"
        type="text"
        placeholder="Select"
        value={searchLineItemInput}
        onChange={(e) => {
          setSearchLineItemInput(e.target.value);
          setSelectedLineItemIndex(-1);
        }}
      />
      {!!searchLineItemInput.length && (
        <div className="w-full bg-white shadow rounded p-2 relative z-10 ">
          {getAutoComplateResults(lineItemsDataFromDataBase, searchLineItemInput, [], selectedLineItemIndex)}
        </div>
      )}
      {selectedLineItem && (
        <>
          <div className="flex relative">
            {selectedLineItem.elements.map((element, index) => {
              if (element.type === "text") {
                return (
                  <div key={index} className="flex gap-2">
                    <div>{element.value}&nbsp;</div>
                  </div>
                );
              } else {
                return (
                  <div key={index} className="flex gap-2">
                    <div>{element.value || "____"}&nbsp;</div>
                  </div>
                );
              }
            })}
            <div
              className="absolute top-0 right-2 bg-sky-100 px-2 py-0.5 rounded-lg btn"
              onClick={async () => {
                onAccept(selectedLineItem);
              }}
            >
              Accept
            </div>
          </div>
          <div className="self-stretch px-6 py-8 justify-start items-start gap-4 inline-flex min-h-[200px]">
            <LineItemPreviewPanel lineItemToProcess={selectedLineItem.elements} onElementValueClick={onElementValueClick} />
          </div>
        </>
      )}
    </div>
  );
});

const XactimateToolPageRow: React.FC<{
  rowData: IXactimateToolRowData;
  existingRows: IXactimateToolRowData[];
  onSave: (row: IXactimateToolRowData) => void;
  onDelete: (id: string) => void;
  onDuplicate: (data: DuplicateMappingDialogData) => void;
}> = observer(({ rowData, existingRows, onSave, onDelete, onDuplicate }) => {
  const firestoreToolsCollectionRef = collection(useFirestore(), SharedUtils.getDbPath("tools")) as CollectionReference<IDatabaseLineItem>;
  const lineItemsDataFromDataBase = useFirestoreCollectionData<IDatabaseLineItem>(firestoreToolsCollectionRef, { idField: "id" });
  const [showChooseRebuild, setShowChooseRebuild] = React.useState(false);
  const [rowDataState, setRowDataState] = useState(rowData);
  const savedRowDataState = useRef(rowData);
  const [saveEnebled, setSaveEnabled] = useState(false);

  useEffect(() => {
    if (JSON.stringify(rowDataState) !== JSON.stringify(savedRowDataState.current)) {
      setSaveEnabled(true);
    } else {
      setSaveEnabled(false);
    }
  }, [rowDataState]);

  if (lineItemsDataFromDataBase.status === "loading") {
    return null;
  }

  return (
    <div className="flex m-2 items-center justify-start">
      <ComboBox
        startValue={rowDataState.cat || ""}
        onChange={(value: string) => {
          setRowDataState({ ...rowDataState, cat: value });
        }}
      />
      <input
        value={rowDataState.variable}
        onChange={(e) => {
          setRowDataState({ ...rowDataState, variable: e.target.value });
        }}
        type="text"
        className="border mx-2"
        placeholder="Variable"
      />
      <select
        className="border"
        defaultValue={"Activity"}
        value={rowDataState.activity}
        onChange={(e) => {
          setRowDataState({ ...rowDataState, activity: e.target.value });
        }}
      >
        <option value={""}>Activity</option>
        <option value={"-"}>- (rem)</option>
        <option value={"+"}>+ (r)</option>
        <option value={"&"}>& (r+r)</option>
        <option value={"m"}>m (mat)</option>
        <option value={"i"}>i (install)</option>
        <option value={"r"}>r (d+r)</option>
      </select>
      <select
        className="border mx-2"
        defaultValue={"Quantity"}
        value={rowDataState.quantity}
        onChange={(e) => {
          setRowDataState({ ...rowDataState, quantity: e.target.value });
        }}
      >
        {["Quantity", ...FORMULA_UNIT_VALUES].map((m, index) => {
          return (
            <option key={index} value={m}>
              {m}
            </option>
          );
        })}
      </select>
      <div className=" mx-2 max-w-80 min-w-80">{rowDataState.acceptedLineString || "Rebuild Line Item placeholder"}</div>
      <button
        className="btn rounded-lg bg-sky-500 text-white  mx-2 p-1"
        onClick={() => {
          setShowChooseRebuild(true);
        }}
      >
        Choose Rebuild line item
      </button>
      <button
        className="btn rounded-lg bg-red-500 text-white  mx-2 p-1"
        onClick={async () => {
          const yes = confirm("Are you sure you want to delete this mapping?");
          if (!yes) return;
          const res = await RestAPI.fetchRebuildAPI(`/rest/tools/xactimateMappings/${rowDataState.id}`, "DELETE");
          if (res === null) {
            onDelete(rowDataState.id);
          } else {
            toastNotify("Error deleting mapping", true);
          }
        }}
      >
        Delete Mapping
      </button>
      <button
        className={`rounded-lg mx-2 p-1 ${saveEnebled ? "btn bg-green-500 text-white  " : "btn bg-gray-300 text-gray-700"}`}
        onClick={async () => {
          const duplicate = findDuplicateMapping(existingRows, rowDataState);
          if (duplicate) {
            onDuplicate({
              action: "save",
              selectedLineItemId: rowDataState.selectedLineItemId,
              acceptedLineString: rowDataState.acceptedLineString,
              existingCat: duplicate.cat,
              existingSel: duplicate.variable,
            });
            return;
          }
          const res = await RestAPI.fetchRebuildAPI(`/rest/tools/xactimateMappings/${rowDataState.id}`, "POST", { mappingData: rowDataState });
          if (res === null) {
            toastNotify("Mapping saved to production");
            savedRowDataState.current = { ...rowDataState };
            setRowDataState({ ...rowDataState });
            onSave(rowDataState);
          } else {
            toastNotify("Error saving mapping", true);
          }
        }}
      >
        Save
      </button>
      {showChooseRebuild && (
        <TopMostPortal>
          <LineItemPreviewPanelXactimate
            xactimateRowData={rowDataState}
            onClose={() => {
              setShowChooseRebuild(false);
            }}
            onAccept={(selectedLineItem) => {
              const newDataState = { ...rowDataState };
              newDataState.acceptedLineString = selectedLineItem.elements.map((e) => e.value).join(" ");
              newDataState.selectedLineItemId = selectedLineItem.id;
              setRowDataState(newDataState);
              setShowChooseRebuild(false);
            }}
            lineItemsDataFromDataBase={lineItemsDataFromDataBase.data || []}
          />
        </TopMostPortal>
      )}
    </div>
  );
});

function getHintFromActivity(act: string) {
  switch (act) {
    case "-":
      return "(rem)";
    case "+":
      return "(r)";
    case "&":
      return "(r+r)";
    case "m":
      return "(mat)";
    case "i":
      return "(install)";
    case "r":
      return "(d+r)";
  }
  return "";
}

const XactimateToolPageFixedRow: React.FC<{
  rowData: IXactimateToolRowData;
  existingRows: IXactimateToolRowData[];
  discardLine: (rowData: IXactimateToolRowData) => void;
  onAcceptMapping: (row: IXactimateToolRowData) => void;
  onDuplicate: (data: DuplicateMappingDialogData) => void;
}> = observer(({ rowData, existingRows, discardLine, onAcceptMapping, onDuplicate }) => {
  if (
    rowData.discard ||
    !rowData.id ||
    !rowData.acceptedLineString ||
    !rowData.selectedLineItemId ||
    !rowData.lineNoSpaces ||
    !rowData.activity ||
    !rowData.quantity ||
    !rowData.variable ||
    !rowData.cat
  ) {
    return null;
  }
  return (
    <div className={`flex m-2 my-4 items-center justify-start`}>
      <div className="border w-24  px-2 mx-2">{rowData.cat}</div>
      <div className="border w-24 px-2 mx-2">{rowData.variable}</div>
      <div className="border w-24 overflow-hidden px-2 mx-2">
        {rowData.activity} {getHintFromActivity(rowData.activity ?? "")}
      </div>
      <div className="border w-24 px-2 mx-2 no-break">{rowData.quantity}</div>
      <div className={`border mx-2 max-w-80 min-w-80 px-2 ${(rowData.variations || 0) > 0 ? "bg-yellow-100" : ""}`}>
        {rowData.acceptedLineString}
      </div>
      <button
        className="btn rounded-lg bg-sky-500 text-white  mx-2 p-1"
        onClick={async () => {
          const newRow: IXactimateToolRowData = {
            ...rowData,
            createdAt: new Date().getTime(),
            id: uuidv4(),
          };
          const duplicate = findDuplicateMapping(existingRows, newRow);
          if (duplicate) {
            onDuplicate({
              action: "accept",
              selectedLineItemId: newRow.selectedLineItemId,
              acceptedLineString: newRow.acceptedLineString,
              existingCat: duplicate.cat,
              existingSel: duplicate.variable,
            });
            return;
          }
          const res = await RestAPI.fetchRebuildAPI(`/rest/tools/xactimateMappings/${newRow.id}`, "POST", { mappingData: newRow });
          if (res === null) {
            toastNotify("Mapping accepted");
            onAcceptMapping(newRow);
          } else {
            toastNotify("Error accepting mapping", true);
          }
        }}
      >
        Accept Mapping
      </button>
      <button
        className="btn rounded-lg bg-red-500 text-white  mx-2 p-1"
        onClick={() => {
          discardLine(rowData);
        }}
      >
        Reject Mapping
      </button>
    </div>
  );
});

export const XactimateToolPage = observer(() => {
  const [rows, setRows] = useState<IXactimateToolRowData[]>([]);
  const [loadingRows, setLoadingRows] = useState(true);
  const [loadingFixedMappings, setLoadingFixedMappings] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [duplicateDialog, setDuplicateDialog] = useState<DuplicateMappingDialogData | null>(null);

  const [fixedMappings, setFixedMappings] = useState<
    {
      id: string;
      collection: string;
      mappings: IXactimateToolRowData[];
    }[]
  >([]);
  const [fixedMappingsFlat, setFixedMappingsFlat] = useState<IXactimateToolRowData[]>([]);

  // Load all mappings progressively from production API
  useEffect(() => {
    (async () => {
      setLoadingRows(true);
      const first = await fetchMappingsPage(1);
      setRows(first.data);

      const totalPages = Math.ceil(first.total / MAPPINGS_LIMIT);
      if (totalPages > 1) {
        const remaining = Array.from({ length: totalPages - 1 }, (_, i) => fetchMappingsPage(i + 2));
        remaining.forEach(async (pagePromise) => {
          const page = await pagePromise;
          setRows((prev) => [...prev, ...page.data]);
        });
      }

      setLoadingRows(false);
    })();
  }, []);

  // Load user-generated mappings for review
  useEffect(() => {
    (async () => {
      setLoadingFixedMappings(true);
      const res = await RestAPI.fetchURL(`${RestAPI.SERVER_BASE_URL}/rest/estimates/getFixedMapping`, "GET");
      if (res && res.length > 0) {
        setFixedMappings(res);
        logDebug("fixedMappings", res);
      }
      setLoadingFixedMappings(false);
    })();
  }, []);

  useEffect(() => {
    if (rows.length > 0 && fixedMappings.length > 0) {
      const fixedMappingsFlat = fixedMappings
        .map((m) => {
          m.mappings.forEach((mapping) => {
            mapping.fixedCollection = m.collection;
          });
          return m.mappings;
        })
        .flat();
      const fixedMappingsFlatFiltered = fixedMappingsFlat.filter(
        (m) => !rows.find((a) => a.lineNoSpaces?.toLowerCase() === m.lineNoSpaces?.toLowerCase()),
      );
      const fixedMappingsFlatAlreadyExists = fixedMappingsFlat.filter((m) =>
        rows.find((a) => a.lineNoSpaces?.toLowerCase() === m.lineNoSpaces?.toLowerCase()),
      );
      logDebug("fixedMappingsFlat", fixedMappingsFlat.length, fixedMappingsFlatFiltered.length, fixedMappingsFlatAlreadyExists.length);
      fixedMappingsFlatFiltered.sort((a, b) => a.lineNoSpaces?.localeCompare(b.lineNoSpaces ?? "") ?? 0);
      const fixedMappingsFlatFilteredNoDuplicates = fixedMappingsFlatFiltered.filter((m, index, arr) => {
        return (
          !m.discard &&
          arr.findIndex(
            (a) =>
              a.lineNoSpaces?.toLowerCase() === m.lineNoSpaces?.toLowerCase() &&
              a.activity?.toLowerCase() === m.activity?.toLowerCase() &&
              a.quantity?.toLowerCase() === m.quantity?.toLowerCase() &&
              a.cat?.toLowerCase() === m.cat?.toLowerCase() &&
              a.variable?.toLowerCase() === m.variable?.toLowerCase(),
          ) === index
        );
      });
      fixedMappingsFlatFilteredNoDuplicates.forEach((m) => {
        if (!m.id) m.id = uuidv4();
        m.variations =
          fixedMappingsFlatFilteredNoDuplicates.filter((m1) => m1.lineNoSpaces?.toLowerCase() === m.lineNoSpaces?.toLowerCase()).length - 1;
      });
      setFixedMappingsFlat(fixedMappingsFlatFilteredNoDuplicates);
    }
  }, [rows, fixedMappings]);

  return (
    <div>
      <MappingTopBox />
      <div className="w-full bg-sky-100 justify-between flex">
        <div>Xactimate mapping tool</div>
      </div>
      <button
        className="my-2 bg-sky-100 p-2 rounded-lg"
        onClick={() => {
          setRows((prev) => [{ id: uuidv4(), createdAt: new Date().getTime() }, ...prev]);
        }}
      >
        Add Row
      </button>

      <div className="relative h-[400px] border overflow-y-scroll">
        {loadingRows && rows.length === 0 ? (
          <LoadingAnimation fullScreen={false} />
        ) : (
          rows
            .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
            .map((rowData) => (
              <XactimateToolPageRow
                key={rowData.id}
                rowData={rowData}
                existingRows={rows}
                onSave={(updated) => setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))}
                onDelete={(id) => setRows((prev) => prev.filter((r) => r.id !== id))}
                onDuplicate={setDuplicateDialog}
              />
            ))
        )}
      </div>
      <div className="w-full bg-sky-100 justify-between flex">
        <div>User Generated Mappings to Review</div>
      </div>
      <div className="relative h-[400px] border overflow-y-scroll">
        {loadingFixedMappings ? (
          <LoadingAnimation fullScreen={false} />
        ) : (
          fixedMappingsFlat.map((rowData, index) => {
            rowData.id = uuidv4();
            rowData.createdAt = new Date().getTime();
            return (
              <XactimateToolPageFixedRow
                key={rowData.id}
                rowData={rowData}
                existingRows={rows}
                onDuplicate={setDuplicateDialog}
                onAcceptMapping={(newRow) => setRows((prev) => [newRow, ...prev])}
                discardLine={async (rowData) => {
                  setLoadingData(true);
                  try {
                    logDebug("discardLine", rowData.fixedCollection, rowData.lineNoSpaces);
                    await RestAPI.fetchURL(`${RestAPI.SERVER_BASE_URL}/rest/estimates/rejectFixedMapping`, "POST", {
                      collection: rowData.fixedCollection,
                      lineNoSpaces: rowData.lineNoSpaces,
                    });
                    setFixedMappingsFlat((old) => {
                      const newData = old.find((r) => r.id === rowData.id);
                      newData && (newData!.discard = true);
                      return [...old];
                    });
                  } catch (error) {
                    toastNotify("Error discarding line item", true);
                  }
                  setLoadingData(false);
                }}
              />
            );
          })
        )}
      </div>

      {loadingData && <LoadingAnimation fullScreen={true} />}
      {duplicateDialog && <DuplicateMappingDialog data={duplicateDialog} onClose={() => setDuplicateDialog(null)} />}
    </div>
  );
});
